/**
 * ACT-1488 (parity item T9) — resolves the `title` prop for `@astrana/ui`'s
 * ClinicalSummary card from the signed-in user's persona template.
 *
 * ## Why this returns undefined by default
 *
 * Copilot used to inject the active persona's clinical-summary template title
 * as the card heading unconditionally. For the PROVIDER and ADMIN personas that
 * resolves to a real, correctly-named DB row —
 * `member_360.ai_template.title = 'Pre-Round Summary'` — so those users saw
 * "Pre-Round Summary" where Atlas/Provider Portal shows "Clinical Summary".
 *
 * Parity wants the literal "Clinical Summary" for every persona. The template
 * row itself is NOT wrong and is used elsewhere (it is a genuine named AI
 * template), so this changes the DISPLAY only: no admin-console migration, and
 * the `currentPersona.clinicalSummaryTemplate { title }` GraphQL selection
 * stays exactly as it is.
 *
 * Returning `undefined` is the mechanism, not an oversight: both `@astrana/ui`
 * props involved (`ClinicalSummary`'s `title` and `PracticeSchedule`'s
 * `summaryTitle`) are optional and documented to fall back to the module's own
 * static "Clinical Summary". So the host simply declines to pass one, and the
 * shared library's default wins — which is why this needs no `@astrana/ui`
 * change at all.
 *
 * ## Why this is NOT named `resolveClinicalSummaryTitle`
 *
 * `@astrana/ui` already exports a function by that name (alongside
 * `DEFAULT_CLINICAL_SUMMARY_TITLE = "Clinical Summary"`, in its
 * ClinicalSummary.utils). That one maps an optional title to the string
 * actually rendered and ALWAYS returns a non-empty label. This one does the
 * opposite job — it decides whether the host supplies a title at all, and
 * returns `undefined` to mean "don't". Sharing the name would invite the
 * assumption that they're interchangeable.
 *
 * ## Why a flag rather than a deletion
 *
 * The per-persona heading is a product decision that may be revisited, and the
 * data to power it is still fetched. Gating instead of deleting keeps it
 * recoverable per-environment by flipping
 * `VITE_FEATURE_PERSONA_CLINICAL_SUMMARY_TITLE` — no code change, no
 * git archaeology to work out why the heading is static.
 *
 * ## Why both call sites share this helper
 *
 * TWO surfaces inject the same persona field, and they must never disagree —
 * the same card showing "Clinical Summary" on Home and "Pre-Round Summary" in a
 * schedule row is worse than either choice alone:
 *
 *  - `MemberProfilePage.tsx` — the Home tab's CLINICAL_SUMMARY card.
 *  - `PracticeSchedulePage.tsx` — each schedule row's Clinical Summary card,
 *    via `PracticeSchedule`'s `summaryTitle` prop.
 *
 * @param personaTemplateTitle The active persona's clinical-summary template
 *   title. Read defensively at both call sites — it is null/undefined while the
 *   CurrentUser query is in flight, when the persona has no template binding,
 *   and whenever `VITE_FEATURE_CLINICAL_SUMMARY_TEMPLATE_TITLE` is off (which
 *   omits the selection from the query document entirely).
 * @param usePersonaTitle `featureFlags.personaClinicalSummaryTitle`.
 * @returns The title to pass through, or `undefined` to let `@astrana/ui` use
 *   its own "Clinical Summary" default.
 */
export function clinicalSummaryTitleOverride(
  personaTemplateTitle: string | null | undefined,
  usePersonaTitle: boolean,
): string | undefined {
  if (!usePersonaTitle) return undefined;
  // Whitespace-only titles are treated as absent rather than rendered — a
  // blank heading is worse than the static default. The pinned @astrana/ui
  // normalises blanks the same way, so this is belt-and-braces: it keeps the
  // host's behaviour identical regardless of the library version pinned.
  const trimmed = personaTemplateTitle?.trim();
  return trimmed ? trimmed : undefined;
}
